import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Table, Form, Alert, Row, Col } from 'react-bootstrap';
import { getCostAllocations, saveCostAllocations } from '../api';
import { toast } from 'react-toastify';

const CostAllocationModal = ({ show, handleClose, year, month: initialMonth, segments }) => {
  const [allocations, setAllocations] = useState({});
  const [loading, setLoading] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(initialMonth || new Date().getMonth() + 1);

  const COST_GROUPS = [
    { id: 'Remuneraciones', label: 'Remuneraciones' },
    { id: 'Gastos Operacionales', label: 'Gastos Op.' },
    { id: 'Otros Ingresos', label: 'Otros Ingresos' },
    { id: 'Impuestos', label: 'Impuestos' }
  ];

  const MESES = [
    { id: 1, name: "Enero" }, { id: 2, name: "Febrero" }, { id: 3, name: "Marzo" },
    { id: 4, name: "Abril" }, { id: 5, name: "Mayo" }, { id: 6, name: "Junio" },
    { id: 7, name: "Julio" }, { id: 8, name: "Agosto" }, { id: 9, name: "Septiembre" },
    { id: 10, name: "Octubre" }, { id: 11, name: "Noviembre" }, { id: 12, name: "Diciembre" }
  ];

  const loadAllocations = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getCostAllocations(year, currentMonth);
      const map = {};
      data.forEach(a => {
        if (!map[a.segment]) map[a.segment] = {};
        map[a.segment][a.cost_group] = a.percentage;
      });
      setAllocations(map);
    } catch (error) {
      console.error("Error loading allocations:", error);
    } finally {
      setLoading(false);
    }
  }, [year, currentMonth]);

  useEffect(() => {
    if (show && year && currentMonth) {
      loadAllocations();
    }
  }, [show, year, currentMonth, loadAllocations]);

  // Sincronizar mes si cambia desde afuera
  useEffect(() => {
    if (initialMonth) setCurrentMonth(initialMonth);
  }, [initialMonth]);

  const handlePercentageChange = (segment, group, value) => {
    const val = parseFloat(value) || 0;
    setAllocations(prev => ({
      ...prev,
      [segment]: {
        ...(prev[segment] || {}),
        [group]: Math.min(100, Math.max(0, val))
      }
    }));
  };

  const handleSave = async () => {
    setLoading(true);
    const payload = [];
    
    Object.keys(allocations).forEach(segment => {
      Object.keys(allocations[segment]).forEach(group => {
        payload.push({
          year,
          month: currentMonth,
          segment,
          cost_group: group,
          percentage: allocations[segment][group]
        });
      });
    });

    try {
      await saveCostAllocations(payload);
      toast.success(`Proporciones de ${MESES.find(m => m.id === currentMonth).name} guardadas`);
      // No cerramos el modal para que pueda seguir editando otros meses
    } catch (error) {
      console.error("Error saving allocations:", error);
    } finally {
      setLoading(false);
    }
  };

  const getGroupTotal = (group) => {
    return Object.values(allocations).reduce((sum, seg) => sum + (seg[group] || 0), 0);
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton className="bg-dark text-white">
        <Modal.Title className="fw-bold fs-5">
          ⚙️ Matriz de Proporcionalidad {year}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="bg-light">
        
        <Row className="mb-3 align-items-center">
            <Col md={6}>
                <Form.Group>
                    <Form.Label className="small fw-bold text-muted uppercase">Mes a configurar:</Form.Label>
                    <Form.Select 
                        value={currentMonth} 
                        onChange={(e) => setCurrentMonth(parseInt(e.target.value))}
                        className="fw-bold border-primary text-primary"
                    >
                        {MESES.map(m => (
                            <option key={m.id} value={m.id}>{m.name}</option>
                        ))}
                    </Form.Select>
                </Form.Group>
            </Col>
            <Col md={6} className="text-end pt-4">
                <Alert variant="info" className="small py-1 mb-0 d-inline-block">
                    Los cambios se guardan por mes.
                </Alert>
            </Col>
        </Row>

        <div className="table-responsive shadow-sm rounded bg-white p-2">
          <Table bordered hover size="sm" className="align-middle mb-0">
            <thead className="bg-light text-center small uppercase fw-bold">
              <tr>
                <th className="text-start ps-3">Segmento</th>
                {COST_GROUPS.map(g => <th key={g.id} style={{width: '110px'}}>{g.label} %</th>)}
              </tr>
            </thead>
            <tbody>
              {segments.map(seg => (
                <tr key={seg}>
                  <td className="fw-bold ps-3">{seg}</td>
                  {COST_GROUPS.map(g => (
                    <td key={g.id}>
                      <Form.Control
                        type="number"
                        size="sm"
                        className="text-end fw-bold no-arrows"
                        value={allocations[seg]?.[g.id] ?? ''}
                        onChange={(e) => handlePercentageChange(seg, g.id, e.target.value)}
                        placeholder="0"
                        style={{border: 'none', background: 'transparent'}}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
            <tfoot className="table-secondary fw-bold">
              <tr>
                <td className="text-end pe-3">TOTAL ASIGNADO:</td>
                {COST_GROUPS.map(g => {
                  const total = getGroupTotal(g.id);
                  return (
                    <td key={g.id} className={`text-end ${total > 100 ? 'text-danger' : total === 100 ? 'text-success' : 'text-muted'}`}>
                      {total}%
                    </td>
                  );
                })}
              </tr>
            </tfoot>
          </Table>
        </div>
        
        {COST_GROUPS.some(g => getGroupTotal(g.id) > 100) && (
          <Alert variant="danger" className="small mt-3 py-1 mb-0">
            ⚠️ <strong>Error:</strong> Algunos grupos superan el 100% de asignación. Por favor corrige los valores.
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer className="bg-light border-top-0">
        <Button variant="outline-secondary" onClick={handleClose}>Cerrar</Button>
        <Button variant="primary" onClick={handleSave} disabled={loading || COST_GROUPS.some(g => getGroupTotal(g.id) > 100)}>
          {loading ? 'Guardando...' : 'Guardar Mes Actual'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CostAllocationModal;
